'use client';

/**
 * GlobalReauthHandler
 *
 * App-wide listener for the `auth:circuit-breaker` event dispatched by the
 * Supabase browser client (lib/supabase/client.ts) when its circuit-breaker
 * trips on a terminal token failure.
 *
 * Without this, ONLY the active-call page (Conference.tsx) and the incident-
 * report page mount a ReauthModal — every other page (admin panel, deep
 * analysis, reports, dashboard, etc.) would force-redirect to /login on a
 * mid-task token expiry, losing all in-flight work. This component shows the
 * same in-place ReauthModal app-wide so the user can re-authenticate without
 * losing context.
 *
 * A70: when the failure says the SERVER ended the session for inactivity
 * (GoTrue GOTRUE_SESSIONS_INACTIVITY_TIMEOUT), the dead session is dropped
 * locally and the officer lands on /login?reason=idle with an honest message.
 * Other server-ended sessions keep the in-place modal above. The
 * server's GOTRUE_SESSIONS_INACTIVITY_TIMEOUT is the only idle clock: there is
 * deliberately no front-end idle timer here.
 *
 * Mounted once in app/template.tsx, rendered on every route except those
 * explicitly bypassed by template.tsx.
 */

import { useEffect, useRef, useState } from 'react';
import { ReauthModal } from '@/components/conference/ReauthModal';
import { createClient } from '@/lib/supabase/client';
import { classifyServerEndedSession } from '@/lib/supabase/refreshFailureClassifier';
import { performSessionEndedSignout } from '@/lib/auth/logout';
import logger from '@/lib/logger';

export function GlobalReauthHandler() {
  const [open, setOpen] = useState(false);
  const signingOutRef = useRef(false);

  useEffect(() => {
    function onCircuitBreaker(ev: Event) {
      const detail = (ev as CustomEvent).detail;
      logger.warn('GlobalReauthHandler', 'Circuit-breaker event received', { detail });

      // Only the server's INACTIVITY timeout navigates away: the officer was idle, so no in-flight work is lost.
      // Any other terminal failure keeps the in-place ReauthModal (re-authenticate without losing context).
      const ended = classifyServerEndedSession(detail);
      if (ended === 'idle') {
        if (signingOutRef.current) return;
        signingOutRef.current = true;
        setOpen(false);
        logger.info('GlobalReauthHandler', 'Server ended the session; returning to sign-in', { reason: ended });
        void performSessionEndedSignout(ended, {
          signOutLocal: () => createClient().auth.signOut({ scope: 'local' }),
        });
        return;
      }
      setOpen(true);
    }
    window.addEventListener('auth:circuit-breaker', onCircuitBreaker as EventListener);
    return () => {
      window.removeEventListener('auth:circuit-breaker', onCircuitBreaker as EventListener);
    };
  }, []);

  if (!open) return null;

  return (
    <ReauthModal
      isOpen={open}
      onClose={() => setOpen(false)}
      onSuccess={() => setOpen(false)}
    />
  );
}
